import { z } from 'zod'

export const TEXT_PROP_KEYS = new Set([
  'title',
  'subtitle',
  'description',
  'label',
  'name',
  'role',
  'bio',
  'comment',
  'caption',
  'intro',
  'tagline',
  'taglineTitle',
  'phone',
  'address',
  'phoneLabel',
  'addressLabel',
  'ctaLabel',
  'buttonLabel',
  'primaryLabel',
  'secondaryLabel',
  'tertiaryLabel',
  'creatorLabel',
  'copyright',
  'logoText',
  'imageAlt',
  'content',
  'text',
  'doctorSelectLabel',
])

export function isLocalizedText(value: unknown): value is LocalizedText {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
  if (keys.length === 0) return false
  return keys.every((k) => typeof record[k] === 'string' || record[k] === undefined)
}

export const localizedTextSchema = z.record(z.string().optional()).refine(
  (value) => Object.keys(value).length > 0,
  { message: 'LocalizedText is empty' },
)

export type LocalizedText = z.infer<typeof localizedTextSchema>

function issue(ctx: z.RefinementCtx, message: string, path: (string | number)[] = []) {
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path })
}

function walkLocalizedProps(
  props: Record<string, unknown>,
  path: Array<string | number>,
  ctx: z.RefinementCtx,
): void {
  for (const [key, value] of Object.entries(props)) {
    const nextPath = [...path, key]
    if (TEXT_PROP_KEYS.has(key)) {
      if (typeof value === 'string') {
        issue(ctx, `${pathToString(nextPath)}: text props must be LocalizedText, got bare string`, nextPath)
      } else if (value != null && !isLocalizedText(value) && typeof value !== 'object') {
        issue(ctx, `${pathToString(nextPath)}: expected LocalizedText`, nextPath)
      } else if (value && isLocalizedText(value)) {
        if (value.fa === undefined && value.en === undefined && Object.keys(value).length === 0) {
          issue(ctx, `${pathToString(nextPath)}: LocalizedText is empty`, nextPath)
        }
      }
    }
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          walkLocalizedProps(item as Record<string, unknown>, [...nextPath, i], ctx)
        }
      })
    } else if (value && typeof value === 'object' && !isLocalizedText(value) && !Array.isArray(value)) {
      walkLocalizedProps(value as Record<string, unknown>, nextPath, ctx)
    }
  }
}

function pathToString(path: Array<string | number>): string {
  return path
    .map((part, i) => (typeof part === 'number' ? `[${part}]` : i === 0 ? String(part) : `.${part}`))
    .join('')
}

export const blockSchema = z.object({
  component: z.string().min(1, 'missing component'),
  enabled: z.boolean({ invalid_type_error: 'enabled must be boolean' }),
  props: z.record(z.unknown(), { invalid_type_error: 'props must be an object' }),
}).superRefine((block, ctx) => {
  walkLocalizedProps(block.props, ['props'], ctx)
})

export const pageSchema = z.object({
  title: localizedTextSchema,
  slug: z.string({ invalid_type_error: 'slug must be a string' }),
  enabled: z.boolean({ invalid_type_error: 'enabled must be boolean' }),
  blocks: z.array(blockSchema, { invalid_type_error: 'blocks must be an array' }),
  description: localizedTextSchema.optional(),
  seoTitle: localizedTextSchema.optional(),
  seoDescription: localizedTextSchema.optional(),
  seoImage: z.string().optional(),
  noindex: z.boolean().optional(),
  template: z.string().optional(),
})

export const siteSchema = z.object({
  schemaVersion: z.number().int().min(1).optional().default(1),
  i18n: z.object({
    enabled: z.boolean({ invalid_type_error: 'i18n.enabled must be boolean' }),
    locales: z.array(z.string()).min(1, 'i18n.locales must be a non-empty array'),
    defaultLocale: z.string({ invalid_type_error: 'i18n.defaultLocale must be a string' }),
  }, { required_error: 'schema.i18n is required' }),
  title: localizedTextSchema,
  siteSettings: z.object({
    themePrimary: z.string({ invalid_type_error: 'siteSettings.themePrimary must be a string' }),
    themeNeutral: z.string({ invalid_type_error: 'siteSettings.themeNeutral must be a string' }),
    fontFamily: z.string({ invalid_type_error: 'siteSettings.fontFamily must be a string' }),
    logoUrl: z.string({ invalid_type_error: 'siteSettings.logoUrl must be a string' }),
    favicon: z.string({ invalid_type_error: 'siteSettings.favicon must be a string' }),
    description: localizedTextSchema.optional(),
  }, { required_error: 'schema.siteSettings is required' }),
  layout: z.object({
    blocks: z.array(blockSchema, { required_error: 'schema.layout.blocks is required' })
      .min(1, 'schema.layout.blocks must not be empty'),
  }, { required_error: 'schema.layout is required' }),
  pages: z.record(pageSchema, { required_error: 'schema.pages is required', invalid_type_error: 'schema.pages is required' }),
}).passthrough().superRefine((schema, ctx) => {
  const components = schema.layout.blocks.map((b) => b.component)
  if (!components.includes('BlockHeader')) {
    issue(ctx, 'schema.layout.blocks must include BlockHeader', ['layout', 'blocks'])
  }
  if (!components.includes('BlockFooter')) {
    issue(ctx, 'schema.layout.blocks must include BlockFooter', ['layout', 'blocks'])
  }
  if (!schema.pages.home) {
    issue(ctx, 'schema.pages.home is required', ['pages', 'home'])
  }
})

export type Block = z.infer<typeof blockSchema>
export type Page = z.infer<typeof pageSchema>
export type SiteSchema = z.infer<typeof siteSchema>

export function parseSiteSchema(input: unknown): SiteSchema {
  const result = siteSchema.safeParse(input)
  if (!result.success) {
    const first = result.error.issues[0]
    throw new Error(first?.message ?? 'invalid site schema')
  }
  return result.data
}

export function safeParseSiteSchema(input: unknown) {
  return siteSchema.safeParse(input)
}
